// Global Instructions Rule Applied!
//
// LP Allocation (LP agents UAT follow-ups PR E) — rebuilt on the canonical
// grid kit (docs/FRONTEND_DESIGN_PATTERNS.md; Asset Transactions is the
// sibling): ViewToolbar ribbon, ViewConfig (columns / filters / sorting from
// cfg.reporting_columns, persisted per user), TableView with infinite
// scroll. Owner acceptance: the LP status filter (Active, Target, Declined …
// singly or combined); Fund, owner, name, agent-drafts and commitment-range
// filters come with the kit.
//
// Bulk actions (owner request 2026-09-08): select rows (or "all N matching"
// the current filters), then apply ONE change to all of them — Agent drafts
// Default / On / Off, or assign one person to one ownership role. The
// server resolves "all matching" through the same row set the grid reads,
// and the writes are admin-gated (cfg.app_admins), so the bar only renders
// for admins.
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Popconfirm, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import type { RootState } from '../../../state_manager/store.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'
import { useLpAllocationReportId } from '../../../state_manager/hooks/useLpAllocationReportId.ts'
import { TABLE_LP_ALLOCATION } from '../../../state_manager/globals.ts'
import { LpAllocationRow } from '../../../state_manager/data_models/relationships_model.ts'
import { AccountOwnerRoleModel } from '../../../state_manager/data_models/account_owner_role_model.ts'
import { useLpAllocation } from '../hooks/useLpAllocation.ts'
import {
  saveAccountOwner,
  endAccountOwner,
  setAccountAgentDrafts,
  bulkSetLpAgentDrafts,
  bulkSetLpOwner
} from '../services/relationshipsService.ts'
import { LpAllocationTable } from './LpAllocationTable.tsx'

const { Text } = Typography

type BulkAction = 'agent_drafts' | 'owner'

export default function LpAllocationPage() {
  const roles = useSelector((state: any) => (state.globalConfigs?.account_owner_roles || []) as AccountOwnerRoleModel[])
  const isAppAdmin = useSelector((state: RootState) => state.globalConfigs?.is_app_admin === true)
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((state: RootState) => state.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])
  const reportId = useLpAllocationReportId()

  const {
    data,
    totalCount,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    refetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    patchRow,
    defaultTableFilters,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useLpAllocation()

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)
  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        { reporting_columns: reportingColumns, reporting_column_defaults: reportingColumnDefaults },
        { primaryModuleKey: 'lp_allocation' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )
  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )
  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    return getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    }).map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])
  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of gridColumns) if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    return m
  }, [addColumnsCatalog, gridColumns])
  const resolveViewConfigColumn = useCallback((valueKey: string) => columnByValueKey.get(valueKey), [columnByValueKey])
  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [persistColumnFieldOrder, resetColumnLayoutToDefault, resolveViewConfigColumn, catalogFieldNameOrder, defaultTableColumnKeys]
  )

  const userOptions = useMemo(
    () => ((filterOptions.system_users__id ?? []) as Array<{ value: string; label: string }>),
    [filterOptions]
  )
  const token = () => getAccessToken(store.getState().auth)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  // ── per-row edits ─────────────────────────────────────────────────────────
  const handleDraftsChange = useCallback(
    async (row: LpAllocationRow, next: boolean | null) => {
      const key = `${row.accounts__id}:drafts`
      setBusyKey(key)
      try {
        await setAccountAgentDrafts(row.accounts__id, next, token())
        patchRow(row.accounts__id, {
          accounts__agent_drafts_enabled: next,
          prog__agent_drafts: next === true ? 'On' : next === false ? 'Off' : 'Default'
        })
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to update agent drafts')
      } finally {
        setBusyKey(null)
      }
    },
    [patchRow]
  )

  const handleAssignOwner = useCallback(
    async (row: LpAllocationRow, role: AccountOwnerRoleModel, userIds: string[]) => {
      const key = `${row.accounts__id}:${role.id}`
      setBusyKey(key)
      try {
        const current = row.prog__owners[role.role_key] ?? []
        const currentIds = current.map((h) => h.system_users__id).filter((id): id is string => Boolean(id))
        const tok = token()
        for (const h of current) {
          if (h.system_users__id && !userIds.includes(h.system_users__id)) await endAccountOwner(h.contact_accounts__id, null, tok)
        }
        for (const uid of userIds) {
          if (!currentIds.includes(uid)) await saveAccountOwner({ account_id: row.accounts__id, system_user_id: uid, owner_role_id: role.id }, tok)
        }
        await refetch()
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to update owner')
      } finally {
        setBusyKey(null)
      }
    },
    [refetch]
  )

  // ── selection + bulk actions ──────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [allMatching, setAllMatching] = useState(false)
  const [bulkAction, setBulkAction] = useState<BulkAction>('agent_drafts')
  const [bulkDrafts, setBulkDrafts] = useState<'default' | 'on' | 'off'>('default')
  const [bulkRoleId, setBulkRoleId] = useState<string | undefined>(undefined)
  const [bulkUserId, setBulkUserId] = useState<string | undefined>(undefined)
  const [bulkBusy, setBulkBusy] = useState(false)

  const targetCount = allMatching ? totalCount : selectedIds.length
  const bulkReady = targetCount > 0 && (bulkAction === 'agent_drafts' || (bulkRoleId && bulkUserId))
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedIds,
      preserveSelectedRowKeys: true,
      onChange: (keys: React.Key[]) => {
        setSelectedIds(keys.map(String))
        setAllMatching(false)
      }
    }),
    [selectedIds]
  )
  const clearSelection = () => {
    setSelectedIds([])
    setAllMatching(false)
  }

  const applyBulk = useCallback(async () => {
    setBulkBusy(true)
    try {
      const targets = { accountIds: allMatching ? null : selectedIds, filters: activeFilters }
      const result =
        bulkAction === 'agent_drafts'
          ? await bulkSetLpAgentDrafts(targets, bulkDrafts === 'on' ? true : bulkDrafts === 'off' ? false : null, token())
          : await bulkSetLpOwner(targets, bulkRoleId as string, bulkUserId as string, token())
      const errors = result.errors ?? []
      toast.success(`Updated ${result.updated} of ${result.targeted} LPs${errors.length ? ` (${errors.length} refused)` : ''}`)
      if (errors.length) showErrorToast(errors.slice(0, 3).map((e) => e.error).join(' · '))
      clearSelection()
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Bulk update failed')
    } finally {
      setBulkBusy(false)
    }
  }, [allMatching, selectedIds, activeFilters, bulkAction, bulkDrafts, bulkRoleId, bulkUserId, refetch])

  const bulkBar = isAppAdmin ? (
    <Space size='small' wrap className='items-center'>
      <Text type={targetCount > 0 ? undefined : 'secondary'} className='text-xs whitespace-nowrap'>
        {allMatching ? `All ${totalCount} matching selected` : `${selectedIds.length} selected`}
      </Text>
      {!allMatching && selectedIds.length > 0 && selectedIds.length < totalCount ? (
        <Button size='small' type='link' className='px-0' onClick={() => setAllMatching(true)}>
          Select all {totalCount} matching
        </Button>
      ) : null}
      {targetCount > 0 ? (
        <Button size='small' type='link' className='px-0' onClick={clearSelection}>
          Clear
        </Button>
      ) : null}
      <Select
        size='small'
        style={{ width: 150 }}
        value={bulkAction}
        onChange={(v: BulkAction) => setBulkAction(v)}
        options={[
          { value: 'agent_drafts', label: 'Set agent drafts' },
          { value: 'owner', label: 'Assign owner' }
        ]}
      />
      {bulkAction === 'agent_drafts' ? (
        <Select
          size='small'
          style={{ width: 110 }}
          value={bulkDrafts}
          onChange={(v: 'default' | 'on' | 'off') => setBulkDrafts(v)}
          options={[
            { value: 'default', label: 'Default' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' }
          ]}
        />
      ) : (
        <>
          <Select
            size='small'
            style={{ width: 150 }}
            placeholder='Role'
            value={bulkRoleId}
            onChange={(v: string) => setBulkRoleId(v)}
            options={roles.map((r) => ({ value: r.id, label: r.label }))}
          />
          <Select
            size='small'
            style={{ width: 180 }}
            placeholder='Person'
            showSearch
            optionFilterProp='label'
            value={bulkUserId}
            onChange={(v: string) => setBulkUserId(v)}
            options={userOptions}
          />
        </>
      )}
      <Popconfirm
        title={`Apply to ${targetCount} LP${targetCount === 1 ? '' : 's'}?`}
        description={
          bulkAction === 'agent_drafts'
            ? `Agent drafts → ${bulkDrafts === 'on' ? 'On' : bulkDrafts === 'off' ? 'Off' : 'Default'} on every targeted LP.`
            : 'Assigns the person to the role on every targeted LP. An exclusive role closes the previous holder.'
        }
        okText='Apply'
        disabled={!bulkReady || bulkBusy}
        onConfirm={applyBulk}
      >
        <Button size='small' type='primary' disabled={!bulkReady} loading={bulkBusy}>
          Apply
        </Button>
      </Popconfirm>
    </Space>
  ) : null

  // ── export (loaded rows) ──────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const rows = data.map((r) => {
        const base: Record<string, unknown> = {
          Account: r.accounts__name,
          'LP status': r.comms_lp_statuses__label ?? '',
          'Agent drafts': r.prog__agent_drafts,
          Funds: r.prog__funds,
          'Closed commitments': r.prog__commitment_total_fmt ?? '',
          Prospects: r.prog__prospects_count
        }
        for (const role of roles)
          base[role.label] = (r.prog__owners[role.role_key] ?? []).map((h) => h.system_users__name).filter(Boolean).join(', ')
        return base
      })
      const res = await exportJsonToExcel({ data: rows, filename: 'LP Allocation', sheetName: 'LP Allocation' } as any, token())
      if (!res.success) showErrorToast(res.error || 'Export failed')
      else toast.success('Export ready')
    } finally {
      setExporting(false)
    }
  }, [data, roles])

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey={TABLE_LP_ALLOCATION}
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) => handleFetch(activeSorting, Array.isArray(filters) ? filters : activeFilters)}
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={defaultTableFilters}
          onResetFilters={handleResetFilters}
          leftSlot={bulkBar}
          beforeAddSlot={
            <>
              <ViewConfig
                moduleTitle='LP Allocation'
                columns={gridColumns}
                filterOptions={filterOptions}
                filterOptionsLoading={filterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'LP', related: 'Related' }}
                columnLayout={columnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => handleSetFilters(filters),
                  onResetToDefaults: async () => handleSetFilters(defaultTableFilters)
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => handleSetSorting(sorting),
                  onResetToDefaults: async () => handleSetSorting(defaultTableSorting)
                }}
              />
              <Button icon={<FontAwesomeIcon icon={faDownload} />} loading={exporting} onClick={handleExport}>
                Export
              </Button>
            </>
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <LpAllocationTable
            data={data}
            loading={dataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            roles={roles}
            userOptions={userOptions}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            busyKey={busyKey}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) =>
              handleSetFilters(Object.entries(filters).map(([key, value]) => ({ key, value })))
            }
            onSortChange={(sortConfig: any) =>
              handleSetSorting(sortConfig ? [{ key: sortConfig.column, direction: sortConfig.direction }] : [])
            }
            onDraftsChange={handleDraftsChange}
            onAssignOwner={handleAssignOwner}
            rowSelection={rowSelection}
          />
        </div>
      </div>
    </div>
  )
}
